import {
  Notification,
  PolicyFilters,
  RiskAiFilter,
  ExchangeOnlinePolicyItem,
  ActionEnum,
  MergedRiskAiFilterRule,
  RiskAiFilterRule,
} from 'src/pages/Policy/utils/types';
import i18nInst from 'src/i18n';
import {
  addEmails,
  checkActionByType,
  checkNotification,
  checkSuspiciousTag,
  removeItem,
  showAddApprovedSenderExceedListLimitNotification,
  showAddApprovedSenderListErrNotification,
} from '@Policy/utils/fns';
import { CorrelatedRuleShallow } from 'src/pages/GlobalSetting/CorrelatedRuleTemplates/types';
import { PolicyModeEnum, PolicyTypeEnum } from '@Policy/utils/constants';
import { AnomalyActionData, raiActionDataSelector } from './selectors';

export const createRiskAiFilterSlice = (
  setPolicy: any,
  getPolicy: any,
  getAll: any
) => {
  const { t } = i18nInst;
  const defaultRaiRuleSubjectTag = t(
    'policy.policyTemplate.tmRiskAiFilters.raiRuleSubjectTag'
  );
  const defaultRaiRuleDisclaimer = t(
    'policy.policyTemplate.tmRiskAiFilters.raiRuleDisclaimer'
  );

  const setRiskAiState = (payload: Partial<RiskAiFilter>) => {
    setPolicy((state: ExchangeOnlinePolicyItem) => ({
      ...state,
      [PolicyFilters.tmRiskAiFilters]: {
        ...state[PolicyFilters.tmRiskAiFilters],
        ...payload,
      },
    }));
  };

  const setRiskAiNtfState = (payload: Partial<Notification>) => {
    setPolicy((state: ExchangeOnlinePolicyItem) => ({
      ...state,
      [PolicyFilters.tmRiskAiFilters]: {
        ...state[PolicyFilters.tmRiskAiFilters],
        notification: {
          ...(state[PolicyFilters.tmRiskAiFilters]?.notification || {}),
          ...payload,
        },
      },
    }));
  };

  const replaceInvalidRiskAiAction = (
    actionData: AnomalyActionData[],
    actions: ActionEnum[]
  ) => {
    actionData.forEach((action) => {
      if (!actions.includes(action.action)) {
        console.log(`Invalid action ${action.threatType}: ${action.action}`);
        action.setAction(actions[0]);
      }
    });
  };

  const replaceInvalidRiskAiRulesAction = (
    rules: MergedRiskAiFilterRule[],
    actions: ActionEnum[]
  ) => {
    const newRules = [...rules];
    newRules.forEach((rule) => {
      if (!actions.includes(rule.raiRuleAction)) {
        console.log(
          `Invalid action for rule ${rule.raiRuleId}: ${rule.raiRuleAction}`
        );
        rule.raiRuleAction = actions[0];
      }
    });
    setPolicy(
      (state: ExchangeOnlinePolicyItem) =>
        ({
          ...state,
          [PolicyFilters.tmRiskAiFilters]: {
            ...state[PolicyFilters.tmRiskAiFilters],
            tmRaiFilterRules: newRules,
          },
        }) satisfies ExchangeOnlinePolicyItem
    );
  };

  const validateRiskAi = () => {
    const currentState = getPolicy();
    const currentStore = getAll();
    const {
      securityRiskActionData,
      phishingActionData,
      spamActionData,
      extraAggressiveActionData,
      aggressiveActionData,
      moderateActionData,
      allRulesActionOptions,
      specifiedRulesActionOptions,
    } = raiActionDataSelector(currentStore);
    currentStore.replaceInvalidAction(securityRiskActionData);
    currentStore.replaceInvalidAction(phishingActionData);
    currentStore.replaceInvalidAction(spamActionData);
    replaceInvalidRiskAiAction(
      extraAggressiveActionData,
      allRulesActionOptions
    );
    replaceInvalidRiskAiAction(aggressiveActionData, allRulesActionOptions);
    replaceInvalidRiskAiAction(moderateActionData, allRulesActionOptions);
    const filter: RiskAiFilter = currentState.tmRiskAiFilters;
    replaceInvalidRiskAiRulesAction(
      filter.tmRaiFilterRules,
      specifiedRulesActionOptions
    );

    const result = {
      [PolicyFilters.tmRiskAiFilters]: {},
    };
    const { notifyMessageMaxLength, notifySubjectMaxLength } =
      currentStore.config.limitation;

    let checkResult: any = null;

    // check for 'confirmed' (phishing) action
    if (filter.raiSuspiciousTagConfirmed !== '') {
      checkResult = checkSuspiciousTag(filter.raiSuspiciousTagConfirmed);
      if (checkResult) {
        result[PolicyFilters.tmRiskAiFilters] = {
          raiSuspiciousTagConfirmed: checkResult,
        };
        return result;
      }
    }

    // check for 'spam' action
    if (filter.raiSuspiciousTagSpam !== '') {
      checkResult = checkSuspiciousTag(filter.raiSuspiciousTagSpam);
      if (checkResult) {
        result[PolicyFilters.tmRiskAiFilters] = {
          raiSuspiciousTagSpam: checkResult,
        };
        return result;
      }
    }

    const severitiesActions = [
      {
        action: filter.raiSuperAggActionSuspicious,
        subjectTag: filter.raiSuperAggSubjectTagSuspicious,
        disclaimer: filter.raiSuperAggDisclaimerSuspicious,
      },
      {
        action: filter.raiSuperAggActionUnwanted,
        subjectTag: filter.raiSuperAggSubjectTagUnwanted,
        disclaimer: filter.raiSuperAggDisclaimerUnwanted,
      },
      {
        action: filter.raiAggActionSuspicious,
        subjectTag: filter.raiAggSubjectTagSuspicious,
        disclaimer: filter.raiAggDisclaimerSuspicious,
      },
      {
        action: filter.raiAggActionUnwanted,
        subjectTag: filter.raiAggSubjectTagUnwanted,
        disclaimer: filter.raiAggDisclaimerUnwanted,
      },
      {
        action: filter.raiConsActionSuspicious,
        subjectTag: filter.raiConsSubjectTagSuspicious,
        disclaimer: filter.raiConsDisclaimerSuspicious,
      },
      {
        action: filter.raiConsActionUnwanted,
        subjectTag: filter.raiConsSubjectTagUnwanted,
        disclaimer: filter.raiConsDisclaimerUnwanted,
      },
    ];

    for (const { action, subjectTag, disclaimer } of severitiesActions) {
      const actionValue =
        action === ActionEnum.tagSubject ? subjectTag : disclaimer;
      if (actionValue === '') continue;
      checkResult = checkActionByType(actionValue, action); // Allow empty value to reduce policy size
      if (checkResult) {
        result[PolicyFilters.tmRiskAiFilters] = checkResult;
        return result;
      }
    }

    // check for 'anomalies' rules actions
    for (const rule of filter.tmRaiFilterRules) {
      const actionValue =
        rule.raiRuleAction === ActionEnum.tagSubject
          ? rule.raiRuleSubjectTag
          : rule.raiRuleDisclaimer;
      if (actionValue === '') continue; // Allow empty value to reduce policy size
      checkResult = checkActionByType(actionValue, rule.raiRuleAction);
      if (checkResult) {
        result[PolicyFilters.tmRiskAiFilters] = checkResult;
        return result;
      }
    }

    // check notification
    checkResult = checkNotification(
      filter.notification,
      notifyMessageMaxLength,
      notifySubjectMaxLength
    );
    if (checkResult) {
      result[PolicyFilters.tmRiskAiFilters] = checkResult;
      return result;
    }
    return;
  };

  const setRiskAiRuleAction = (ruleId: string, action: ActionEnum) => {
    const rai: RiskAiFilter = getPolicy().tmRiskAiFilters;
    const rules = rai.tmRaiFilterRules || [];
    const newRules = rules.map((rule) => {
      const newRule = { ...rule };
      if (newRule.raiRuleId === ruleId) {
        newRule.raiRuleAction = action;
        if (action !== ActionEnum.tagSubject) {
          newRule.raiRuleSubjectTag = '';
        } else if (newRule.raiRuleSubjectTag === '') {
          newRule.raiRuleSubjectTag = defaultRaiRuleSubjectTag;
        }
        if (action !== ActionEnum.addDisclaimer) {
          newRule.raiRuleDisclaimer = '';
        } else if (newRule.raiRuleDisclaimer === '') {
          newRule.raiRuleDisclaimer = defaultRaiRuleDisclaimer;
        }
      }
      return newRule;
    });
    setRiskAiState({ tmRaiFilterRules: newRules });
  };

  const createMergedRiskAiFilterRule = (
    rule: CorrelatedRuleShallow,
    data?: RiskAiFilterRule
  ): MergedRiskAiFilterRule => {
    const isMonitor = [
      PolicyModeEnum.DefaultMonitorPolicy,
      PolicyModeEnum.DefaultXDRMonitorPolicy,
    ].includes(getPolicy().policyMode);
    const isGmail = [PolicyTypeEnum.Gmail, PolicyTypeEnum.GmailInline].includes(
      getPolicy().policyType
    );
    const action = isMonitor
      ? ActionEnum.pass
      : (data?.raiRuleAction ??
        (isGmail ? ActionEnum.labelEmail : ActionEnum.tagSubject));
    let subjectTag = ''; // Reduce size by allowing empty value
    let disclaimer = ''; // Reduce size by allowing empty value
    // Replace empty strings with default values or keep the existing value if it's not empty for the subject tag and disclaimer
    if (action === ActionEnum.tagSubject) {
      subjectTag =
        data?.raiRuleSubjectTag === ''
          ? defaultRaiRuleSubjectTag
          : (data?.raiRuleSubjectTag ?? defaultRaiRuleSubjectTag);
    } else if (action === ActionEnum.addDisclaimer) {
      disclaimer =
        data?.raiRuleDisclaimer === ''
          ? defaultRaiRuleDisclaimer
          : (data?.raiRuleDisclaimer ?? defaultRaiRuleDisclaimer);
    }
    return {
      cfgIndex: data?.cfgIndex ?? '',
      raiRuleId: data?.raiRuleId ?? rule.raiRuleId,
      raiRuleAction: action,
      raiRuleSubjectTag: subjectTag,
      raiRuleDisclaimer: disclaimer,
      tmRaiRule: rule,
    };
  };

  const addRiskAiApprovedSender = (email: string | string[]) => {
    const currentState = getPolicy();
    const currentStore = getAll();
    const includeDomain = false;
    const separator = ';';
    const {
      tmRiskAiFilters: { raiApprovedSenderEmailEntries },
    } = currentState;
    const {
      config: { limitation },
    } = currentStore;
    const isSupportWildcard = true;
    const { errList, entriesStr, exceedListLimit } = addEmails(
      email,
      raiApprovedSenderEmailEntries,
      includeDomain,
      limitation.singleApprovedListMaxCount,
      separator,
      limitation.approvedListMaxCount,
      isSupportWildcard
    );
    setPolicy((state: ExchangeOnlinePolicyItem) => ({
      ...state,
      tmRiskAiFilters: {
        ...state.tmRiskAiFilters,
        raiApprovedSenderEmailEntries: entriesStr,
      },
    }));
    // process exceedLimit
    if (exceedListLimit) {
      showAddApprovedSenderExceedListLimitNotification(
        limitation.approvedListMaxCount
      );
    }
    // process error list
    if (errList.length) {
      console.error('addRiskAiApprovedSender error list:', errList);
      showAddApprovedSenderListErrNotification(errList);
    }
  };

  const removeRiskAiApprovedSender = (email: string) => {
    const deleteEmail = email.trim();
    if (deleteEmail === '') {
      return;
    }
    const currentState = getPolicy();
    const {
      tmRiskAiFilters: { raiApprovedSenderEmailEntries },
    } = currentState;
    const separator = ';';
    const result = removeItem(
      deleteEmail,
      raiApprovedSenderEmailEntries,
      separator
    );
    setPolicy((state: ExchangeOnlinePolicyItem) => ({
      ...state,
      tmRiskAiFilters: {
        ...state.tmRiskAiFilters,
        raiApprovedSenderEmailEntries: result,
      },
    }));
  };

  const importRiskAiApprovedSender = (emails: string[]) => {
    const currentState = getPolicy();
    const currentStore = getAll();
    const separator = ';';
    const {
      tmRiskAiFilters: { raiApprovedSenderEmailEntries },
    } = currentState;
    const {
      config: { limitation },
    } = currentStore;
    const importEmails = emails.filter((email) => email.length > 0);
    const existedEntries = raiApprovedSenderEmailEntries
      .split(separator)
      .filter((email: string) => email.length > 0);

    // process exceedLimit
    if (
      importEmails.length + existedEntries.length >
      limitation.approvedListMaxCount
    ) {
      showAddApprovedSenderExceedListLimitNotification(
        limitation.approvedListMaxCount
      );
      return;
    }
    // add emails
    currentStore.addRiskAiApprovedSender(importEmails);
  };

  return {
    setRiskAiState,
    setRiskAiNtfState,
    validateRiskAi,
    setRiskAiRuleAction,
    createMergedRiskAiFilterRule,
    addRiskAiApprovedSender,
    removeRiskAiApprovedSender,
    importRiskAiApprovedSender,
  };
};
